import { motion, useReducedMotion } from 'motion/react'
import { ArrowUpRight, Bot, Building2, Sparkles } from 'lucide-react'
import { creator } from '@/config/creator'

const principles = [
  { icon: Sparkles, title: 'Start with an idea', text: 'I want software creation to begin with what you want to build, not with the technical background you have.' },
  { icon: Bot, title: 'Work with an AI team', text: 'I bring specialized AI employees together to turn a conversation into a designed, coded and shipped product.' },
  { icon: Building2, title: 'Make collaboration visible', text: 'I give your team a dedicated 3D office, making the process of building software feel tangible and collaborative.' },
]
const roles = ['Project manager', 'Designer', 'Engineer', 'QA specialist']

export default function PlatformSection() {
  const reduceMotion = useReducedMotion()

  return (
    <section id="platform" aria-labelledby="platform-heading" className="creator-section">
      <div className="creator-container">
        <motion.div initial={reduceMotion ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: reduceMotion ? 0 : 0.5 }} className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="bento-card bg-primary p-7 text-primary-foreground sm:p-10 lg:col-span-5">
            <p className="creator-label">What I’m building / Coderblock.ai</p>
            <h2 id="platform-heading" className="creator-display mt-5 text-4xl sm:text-5xl">Your idea.<br />An AI team.<br />A real product.</h2>
            <p className="mt-6 text-lg leading-8">I’m building a vibe coding platform where a team of AI employees builds your full-stack app inside a dedicated 3D office.</p>
            <p className="mt-4 text-base leading-7">My vision is simple: make useful software accessible to anyone, with no technical background required.</p>
            <a href={creator.companyUrl} target="_blank" rel="noopener noreferrer" className="creator-button secondary mt-7 motion-reduce:transform-none">Explore Coderblock.ai <ArrowUpRight size={18} aria-hidden="true" /></a>
          </div>
          <div className="bento-card flex min-w-0 flex-col justify-center bg-secondary p-5 sm:p-8 lg:col-span-7">
            <figure className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="flex min-h-11 items-center gap-2 border-b border-border bg-muted px-4">
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-primary" />
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-accent" />
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-secondary-foreground" />
                <span className="ml-2 truncate text-xs font-medium text-muted-foreground">coderblock.ai</span>
              </div>
              <img src="/images/coderblock-preview.jpg" alt="Coderblock official product preview" width={1600} height={1000} loading="lazy" decoding="async" className="h-auto w-full object-cover" />
              <figcaption className="border-t border-border px-5 py-4 text-sm font-medium text-foreground">Official Coderblock preview</figcaption>
            </figure>
            <div className="mt-6">
              <p className="creator-label text-foreground">Meet the AI team</p>
              <ul className="mt-3 flex flex-wrap gap-2" aria-label="AI team roles">
                {roles.map(role => <li key={role} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground">{role}</li>)}
              </ul>
            </div>
          </div>
          {principles.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bento-card bg-card p-6 sm:p-8 lg:col-span-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-foreground"><Icon size={24} aria-hidden="true" /></span>
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">{title}</h3>
              <p className="mt-3 text-base leading-7 text-muted-foreground">{text}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
